import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const isLive = (s) => s === "active" || s === "authenticated";

export const SubscriptionsTable = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/subscriptions"); setSubs(data.subscriptions || []); }
    catch (err) { toast.error(extractError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (s, action) => {
    if (action === "cancel" && !window.confirm(`Cancel ${s.user_email}'s subscription to ${s.tool_name}? Access stops immediately and they will be emailed.`)) return;
    setBusyId(s.subscription_id);
    try {
      await api.post(`/admin/subscriptions/${s.subscription_id}/${action}`);
      toast.success(action === "cancel" ? "Subscription cancelled" : "Synced with Razorpay");
      load();
    } catch (err) { toast.error(extractError(err)); } finally { setBusyId(""); }
  };

  const active = subs.filter((s) => isLive(s.status)).length;

  return (
    <div data-testid="admin-subscriptions">
      <h2 className="font-display text-2xl font-bold">Auto-debit subscriptions</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4"><span className="text-primary font-mono" data-testid="subs-active-count">{active}</span> active of {subs.length}. Cancelling stops access right away and emails the user.</p>
      {loading ? <div className="text-sm text-muted-foreground font-mono">Loading...</div> : (
        <div className="border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Email</TableHead><TableHead>Tool</TableHead><TableHead>Status</TableHead>
              <TableHead>Access until</TableHead><TableHead>Started</TableHead><TableHead>Subscription ID</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {subs.map((s) => (
                <TableRow key={s.subscription_id} data-testid={`sub-row-${s.subscription_id}`}>
                  <TableCell className="font-mono text-xs">{s.user_email}<div className="text-muted-foreground">{s.user_name}</div></TableCell>
                  <TableCell className="text-sm">{s.tool_name}</TableCell>
                  <TableCell className={`font-mono text-xs uppercase tracking-widest ${isLive(s.status) ? "text-primary" : "text-destructive"}`} data-testid={`sub-status-${s.subscription_id}`}>{s.status}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{fmt(s.access_until)}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{fmt(s.activated_at || s.created_at)}</TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">{s.subscription_id}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="rounded-sm h-8" disabled={busyId === s.subscription_id} onClick={() => act(s, "sync")} data-testid={`sub-sync-${s.subscription_id}`}>Sync</Button>
                      {isLive(s.status) && <Button size="sm" variant="outline" className="rounded-sm h-8 text-destructive hover:text-destructive" disabled={busyId === s.subscription_id} onClick={() => act(s, "cancel")} data-testid={`sub-cancel-${s.subscription_id}`}>Cancel</Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {subs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No subscriptions yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
